import { ExclamationTriangleIcon } from '@heroicons/react/24/outline';
import { memo, useState } from 'react';
import type { ObjectDefinitionIssue } from 'shared-types';
import { T } from '../../translation/src';
import type { EditorSelection } from './document/EditorStore';
import { useEditorState, useObjectEditor } from './EditorContext';

/** What an issue path points at, so clicking it selects the culprit. */
function selectionForIssue(path: string, spec: ReturnType<typeof useEditorState>['spec']): EditorSelection | null {
  const match = /^(shapes|groups|params)\[(\d+)\]/.exec(path);
  if (!match) return null;
  const index = Number(match[2]);
  if (match[1] === 'shapes' && spec.shapes[index]) return { kind: 'shapes', ids: [spec.shapes[index].id] };
  if (match[1] === 'groups' && spec.groups[index]) return { kind: 'group', id: spec.groups[index].id };
  if (match[1] === 'params' && spec.params[index]) return { kind: 'param', key: spec.params[index].key };
  return null;
}

/** Validation problems of the draft (it isn't saved while there are any); click one to jump to it. */
const ProblemsBar_: React.FC<{ issues: readonly ObjectDefinitionIssue[] }> = ({ issues }) => {
  const { store } = useObjectEditor();
  const { spec } = useEditorState();
  const [open, setOpen] = useState(true);
  if (issues.length === 0) return null;
  return (
    <div className="absolute bottom-3 left-1/2 z-10 w-[min(36rem,90%)] -translate-x-1/2 rounded-lg border border-warning-yellow-500/60 bg-warning-yellow-200/95 text-sm text-gray-900 shadow-md">
      <button type="button" onClick={() => setOpen((o) => !o)} className="flex w-full items-center gap-2 px-3 py-2 text-left font-medium">
        <ExclamationTriangleIcon className="h-5 w-5 shrink-0" />
        <T _str="{count} problems keep the draft from being saved" args={{ count: issues.length }} swc />
      </button>
      {open && (
        <ul className="max-h-40 overflow-y-auto border-t border-warning-yellow-500/40 px-3 py-1">
          {issues.map((issue, i) => {
            const target = selectionForIssue(issue.path, spec);
            return (
              <li key={i}>
                <button type="button" disabled={!target} onClick={() => target && store.select(target)} className="w-full py-0.5 text-left enabled:hover:underline">
                  <span className="font-mono text-xs">{issue.path}</span> — {issue.message}
                </button>
              </li>
            );
          })}
        </ul>
      )}
    </div>
  );
};

export const ProblemsBar = memo(ProblemsBar_);
